import { useDraggable } from "@dnd-kit/core";
import { RAID_ROLE_ICONS } from "@/shared/lib/roleIcons";
import type { RaidRole } from "@/shared/lib/roles";
import { cn } from "@/shared/lib/utils";
import { classColorStyle } from "@/shared/lib/wowClass";
import type { BoardCharacter } from "../splits.types";

// Pure-visual card: role icon + the owning player's class-colored name. The player's
// main character carries a small "Main" pill on the right; alts carry none. Class colour
// on the name keeps the class signal. `highlighted` shows the amber swap-conflict ring.
// `dense` is the compact variant used in the unassigned pool's chip grid. The DragOverlay
// renders this bare.
export function CharacterCard({
  character,
  highlighted,
  dense,
}: {
  character: BoardCharacter;
  highlighted?: boolean;
  dense?: boolean;
}) {
  const RoleIcon = character.raidRole
    ? RAID_ROLE_ICONS[character.raidRole as RaidRole]
    : null;

  return (
    <div
      className={cn(
        "flex items-center border border-border bg-card hover:bg-muted/40",
        dense
          ? "gap-1.5 rounded px-1.5 py-1 text-xs"
          : "gap-2 rounded-md px-2.5 py-1.5 text-sm",
        highlighted && "ring-2 ring-amber-500",
      )}
    >
      {RoleIcon ? (
        <RoleIcon
          className={cn(
            "shrink-0 text-muted-foreground",
            dense ? "size-3.5" : "size-4",
          )}
        />
      ) : (
        <span
          className={dense ? "size-3.5 shrink-0" : "size-4 shrink-0"}
          aria-hidden
        />
      )}
      <span
        className="min-w-0 flex-1 truncate font-medium"
        style={classColorStyle(character.wowClass)}
      >
        {character.playerName}
      </span>
      {character.isMain && (
        <span
          className={cn(
            "ml-auto shrink-0 rounded bg-muted font-medium text-muted-foreground",
            dense ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]",
          )}
        >
          Main
        </span>
      )}
    </div>
  );
}

// Draggable wrapper mounted inside the board's DndContext. Mirrors SetupBoard's
// DraggableRow: composes dnd-kit listeners onto the card node and dims it while dragging.
export function DraggableCharacterCard({
  character,
  highlighted,
  dense,
  remoteDragging,
}: {
  character: BoardCharacter;
  highlighted?: boolean;
  dense?: boolean;
  // Another officer is dragging this card right now — dim it identically to a local drag.
  remoteDragging?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: character.id,
    data: { character },
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "cursor-default",
        (isDragging || remoteDragging) && "opacity-50",
      )}
      {...listeners}
      {...attributes}
    >
      <CharacterCard
        character={character}
        highlighted={highlighted}
        dense={dense}
      />
    </div>
  );
}
